<template>
  <div class="orcamentos-modal">
    <!-- Main Modal -->
    <!-- Using new sizing: compactDesktop = narrow on desktop (40rem), full-width on mobile (95vw) -->
    <modal
      elem-id="modal_crud_orcamento"
      size="compactDesktop"
      :title="modalTitle"
      :close-label="t('Fechar')"
      :save-label="t('Salvar orçamento')"
      :save-disabled-fn="() => !canSave"
      :save-fn="handleSave"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal"
    >
      <!-- Content only renders when modal is visible (prevents API calls on mount) -->
      <template v-if="isVisible">
        <!-- Tabs (only for edit mode) -->
      <Tabs v-if="isEditMode && orcamentoId > 0" v-model:value="activeTabIndex" :scrollable="false">
        <TabList>
          <Tab :value="0">{{ t('Editar orçamento') }}</Tab>
          <Tab :value="1">{{ t('Serviços do orçamento') }}</Tab>
          <Tab :value="2">{{ t('Compromissos') }}</Tab>
        </TabList>
        <TabPanels>
          <!-- Tab 1: Edit Budget -->
          <TabPanel :value="0">
            <orcamentos-form
              ref="orcamentosFormRef"
              :is-edit-mode="isEditMode"
              @save-success="handleSaveSuccess"
              @validation-error="handleValidationError"
              @cancel-orcamento="handleCancelOrcamento"
              @delete-orcamento="handleDeleteOrcamento"
              @reactivate-orcamento="handleReactivateOrcamento"
              @download-pdf="handleDownloadPdf"
            />
          </TabPanel>

          <!-- Tab 2: Budget Services -->
          <TabPanel :value="1">
            <inline-message severity="warn" :closable="false" class="services-warning">
              {{ t('Se você cancelar todos os serviços deste orçamento, o orçamento será automaticamente cancelado.') }}
            </inline-message>

            <Button
              v-if="activeTabIndex === 1"
              id="btnAddServicoOrcamento"
              data-testid="orcamento-add-service-btn"
              :label="t('Adicionar serviço')"
              icon="pi pi-plus"
              severity="primary"
              size="small"
              @click="handleNovoOrcamentoServico"
              class="add-service-button"
            />

            <!-- New Vue OrcamentoServico Table -->
            <orcamento-servico-table
              v-if="orcamentoId > 0"
              ref="orcamentoServicoTableRef"
              :orcamento-id="orcamentoId"
              @edit-servico="handleEditOrcamentoServico"
            />
          </TabPanel>

          <!-- Tab 3: Compromissos -->
          <TabPanel :value="2">
            <!-- New Vue CompromissosDoOrcamento Table -->
            <compromissos-do-orcamento-table
              v-if="orcamentoId > 0"
              ref="compromissosDoOrcamentoTableRef"
              :orcamento-id="orcamentoId"
              @edit-compromisso="handleEditCompromisso"
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <!-- No tabs for new budget -->
      <div v-else>
        <orcamentos-form
          ref="orcamentosFormRef"
          :is-edit-mode="false"
          @save-success="handleSaveSuccess"
          @validation-error="handleValidationError"
          @cancel-orcamento="handleCancelOrcamento"
          @delete-orcamento="handleDeleteOrcamento"
          @reactivate-orcamento="handleReactivateOrcamento"
          @download-pdf="handleDownloadPdf"
        />
      </div>
      </template>
    </modal>

    <!-- Cancel Confirmation Modal -->
    <modal
      elem-id="modalCancelarOrcamento"
      size="small"
      :title="t('Cancelar orçamento')"
      :close-label="t('Sair')"
      :save-label="t('Cancelar orçamento')"
      :save-fn="confirmCancelOrcamento"
      :show="showCancelModal"
      save-button-class="btn-danger"
      @close="closeCancelModal"
      @dismiss="closeCancelModal"
    >
      <h3>{{ t('Tem certeza que deseja cancelar o orçamento?') }}</h3>
      <p>{{ t('Caso haja um ou mais compromissos, eles também serão cancelados.') }}</p>
    </modal>

    <!-- Depois de salvar um orçamento novo: aprovar e agendar já (chamado DS 2026-0068) -->
    <modal
      elem-id="modalAgendarOrcamentoNovo"
      size="small"
      :title="t('Orçamento salvo')"
      :close-label="t('Fechar')"
      :save-label="t('Aprovar e agendar agora')"
      :save-disabled-fn="() => agendarAgora.motivos.length > 0"
      :save-fn="confirmAgendarAgora"
      :show="agendarAgora.show"
      @close="closeAgendarAgora"
      @dismiss="closeAgendarAgora"
    >
      <p>{{ t('O orçamento fica Pendente até ser aprovado. Aprovar envia o orçamento para a agenda.') }}</p>
      <inline-message v-if="agendarAgora.motivos.length > 0" severity="warn" :closable="false" data-testid="agendar-agora-motivos">
        {{ t('Para agendar, complete o cadastro do cliente') }}: {{ motivosTexto(agendarAgora.motivos) }}
      </inline-message>
    </modal>

    <!-- Delete Confirmation Modal -->
    <modal
      elem-id="modalExcluirOrcamento"
      size="small"
      :title="t('Excluir orçamento')"
      :close-label="t('Sair')"
      :save-label="t('Excluir orçamento')"
      :save-fn="confirmDeleteOrcamento"
      :show="showDeleteModal"
      save-button-class="btn-danger"
      @close="closeDeleteModal"
      @dismiss="closeDeleteModal"
    >
      <h3>{{ t('Tem certeza que deseja excluir o orçamento?') }}</h3>
      <p>{{ t('Caso haja um ou mais compromissos, eles também serão excluídos.') }}</p>
    </modal>

    <!-- OrcamentoServicoModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOrcamento } from '../../composables/useOrcamento';
import { useAgendarOrcamento } from '../../composables/useAgendarOrcamento';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import OrcamentosForm from './OrcamentosForm.vue';
import OrcamentoServicoTable from '../orcamentoservico/OrcamentoServicoTable.vue';
// OrcamentoServicoModal is centralized in ModalManager.vue - opened via eventBus
import CompromissosDoOrcamentoTable from '../compromisso/CompromissosDoOrcamentoTable.vue';

type Id = number | string;

interface OrcamentoData {
  orcamento_franquia_id?: Id;
  [key: string]: unknown;
}

interface OrcamentoFormInstance {
  canSave?: boolean;
  orcamentoData?: OrcamentoData;
  faltaParaAgendar?: string[];
  initializeForNew: () => Promise<void>;
  loadForEdit: (id: Id) => Promise<void>;
  loadForClient: (id: Id) => Promise<void>;
  resetForm: () => void;
  saveBudget: () => Promise<void>;
  refreshServicos: () => void;
}

interface TableInstance {
  loadData?: () => void;
  autoSizeColumns?: () => void;
  onTableVisible?: () => void;
}

interface OrcamentoModalOpenPayload {
  id?: Id;
  clienteId?: Id;
}

interface SaveResult {
  message?: string;
  dados?: { orcamento_id?: number };
}

interface OrcamentoFetchResponse {
  dados: {
    orcamento: Record<string, unknown>;
    cliente: Record<string, unknown>;
    servicos?: Array<Record<string, unknown>>;
    localidade?: string;
  };
}

// Emits
const emit = defineEmits<{
  (e: 'edit-compromisso', id: Id): void;
  (e: 'start-tour'): void;
  (e: 'download-pdf', id: Id): void;
}>();

// Composables
const { t, n } = useI18n();
const { cancelOrcamento, deleteOrcamento, changeStatus, downloadOrcamentoPDF, fetchOrcamento } = useOrcamento();
const { handleAgendar, motivosTexto } = useAgendarOrcamento();

// Refs
const orcamentosFormRef = ref<OrcamentoFormInstance | null>(null);
const orcamentoServicoTableRef = ref<TableInstance | null>(null);
// orcamentoServicoModalRef removed - modal is centralized in ModalManager.vue
const compromissosDoOrcamentoTableRef = ref<TableInstance | null>(null);

// Reactive data
const isVisible = ref<boolean>(false);
const showCancelModal = ref<boolean>(false);
const showDeleteModal = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const orcamentoId = ref<number>(0);
const clienteId = ref<number>(0);
const activeTabIndex = ref<number>(0); // 0 = edit, 1 = servicos, 2 = compromissos
const pendingCancelId = ref<Id | null>(null);
const pendingDeleteId = ref<Id | null>(null);
const agendarAgora = ref<{ show: boolean; orcamentoId: number; motivos: string[] }>({ show: false, orcamentoId: 0, motivos: [] });

// Computed
const modalTitle = computed<string>(() => {
  return isEditMode.value && orcamentoId.value > 0
    ? t('Editar orçamento')
    : t('Novo orçamento');
});

const canSave = computed<boolean>(() => {
  return orcamentosFormRef.value?.canSave || false;
});

const orcamentoFranquiaId = computed<Id>(() => {
  // Get franchise ID from the loaded orcamento form
  if (orcamentosFormRef.value && orcamentosFormRef.value.orcamentoData) {
    return orcamentosFormRef.value.orcamentoData.orcamento_franquia_id || 0;
  }
  return 0;
});

// Methods
const openModal = async (): Promise<void> => {
  log('OrcamentosModal', 'openModal', 'Opening modal for new budget');

  isEditMode.value = false;
  orcamentoId.value = 0;
  clienteId.value = 0;
  activeTabIndex.value = 0;
  isVisible.value = true;

  // Wait for form component to render before initializing
  await nextTick();

  if (orcamentosFormRef.value) {
    await orcamentosFormRef.value.initializeForNew();
  }
};

const loadForEdit = async (budgetId: Id): Promise<void> => {
  log('OrcamentosModal', 'loadForEdit', 'Loading budget for edit', budgetId);

  isEditMode.value = true;
  // Convert to number to ensure proper prop type validation
  orcamentoId.value = Number(budgetId);
  activeTabIndex.value = 0;
  isVisible.value = true;

  // Note: Loading indicator is automatically handled by httpService global interceptor
  try {
    // Wait for the modal and form to be rendered
    await nextTick();

    log('OrcamentosModal', 'loadForEdit', 'After nextTick, orcamentosFormRef.value:', orcamentosFormRef.value);

    // Load form data (httpService shows progress bar automatically)
    if (orcamentosFormRef.value) {
      log('OrcamentosModal', 'loadForEdit', 'Calling form loadForEdit');
      await orcamentosFormRef.value.loadForEdit(budgetId);
    } else {
      log('OrcamentosModal', 'loadForEdit', 'ERROR: orcamentosFormRef.value is still null after nextTick');
    }
  } catch (error) {
    log('OrcamentosModal', 'loadForEdit', 'Error', error);
  }
};

const loadForClient = async (customerId: Id): Promise<void> => {
  log('OrcamentosModal', 'loadForClient', 'Loading for client', customerId);

  isEditMode.value = false;
  orcamentoId.value = 0;
  clienteId.value = Number(customerId);
  activeTabIndex.value = 0;
  isVisible.value = true;

  // Wait for modal and form to be rendered
  await nextTick();

  // Note: Loading indicator is automatically handled by httpService global interceptor
  try {
    // Load client data into form (httpService shows progress bar automatically)
    if (orcamentosFormRef.value) {
      log('OrcamentosModal', 'loadForClient', 'Form ref found, calling loadForClient');
      await orcamentosFormRef.value.loadForClient(customerId);
    } else {
      log('OrcamentosModal', 'loadForClient', 'ERROR: Form ref not found!');
    }
  } catch (error) {
    log('OrcamentosModal', 'loadForClient', 'Error', error);
    eventBus.emit('globalMessage.error', 'Error al cargar datos del cliente');
  }
};

const closeModal = (): void => {
  log('OrcamentosModal', 'closeModal', 'Closing modal');
  // Reset and cleanup when closing
  resetForm();
  // Destroy intl tel input if exists
  const destroyEvent = new CustomEvent('intl-tel.destroy', {
    detail: { elemId: 'telefoneCliente' }
  });
  document.dispatchEvent(destroyEvent);
};

const closeCancelModal = (): void => {
  log('OrcamentosModal', 'closeCancelModal', 'Closing cancel modal');
  showCancelModal.value = false;
  pendingCancelId.value = null;
};

const closeDeleteModal = (): void => {
  log('OrcamentosModal', 'closeDeleteModal', 'Closing delete modal');
  showDeleteModal.value = false;
  pendingDeleteId.value = null;
};

const resetForm = (): void => {
  log('OrcamentosModal', 'resetForm', 'Resetting form');

  if (orcamentosFormRef.value) {
    orcamentosFormRef.value.resetForm();
  }

  isVisible.value = false;
  isEditMode.value = false;
  orcamentoId.value = 0;
  clienteId.value = 0;
  activeTabIndex.value = 0;
};

const handleSave = async (): Promise<void> => {
  log('OrcamentosModal', 'handleSave', 'Save triggered');

  if (orcamentosFormRef.value) {
    await orcamentosFormRef.value.saveBudget();
  }
};

const handleSaveSuccess = (result?: SaveResult): void => {
  log('OrcamentosModal', 'handleSaveSuccess', 'Save success', result);

  // Show success message
  eventBus.emit('globalMessage.success', result?.message || t('Orçamento salvo com sucesso'));

  // Orçamento novo: em vez de fechar calado, oferece aprovar e agendar já.
  const novoId = Number(result?.dados?.orcamento_id ?? 0);
  if (!isEditMode.value && novoId > 0) {
    agendarAgora.value = { show: true, orcamentoId: novoId, motivos: [...(orcamentosFormRef.value?.faltaParaAgendar ?? [])] };
    closeModal();
    return;
  }

  // Close modal after delay
  setTimeout(() => {
    closeModal();
  }, 3000);
};

const closeAgendarAgora = (): void => {
  agendarAgora.value = { show: false, orcamentoId: 0, motivos: [] };
};

const confirmAgendarAgora = async (): Promise<void> => {
  const id = agendarAgora.value.orcamentoId;
  closeAgendarAgora();
  await handleAgendar(id);
};

const handleValidationError = (): void => {
  log('OrcamentosModal', 'handleValidationError', 'Validation error');
  eventBus.emit('globalMessage.error', t('Erro de validação'));
};

const handleCancelOrcamento = (budgetId: Id): void => {
  log('OrcamentosModal', 'handleCancelOrcamento', 'Cancel requested', budgetId);
  pendingCancelId.value = budgetId;
  showCancelModal.value = true;
};

const confirmCancelOrcamento = async (): Promise<void> => {
  log('OrcamentosModal', 'confirmCancelOrcamento', 'Confirming cancel', pendingCancelId.value);

  if (!pendingCancelId.value) return;

  // Note: Loading indicator is automatically handled by httpService global interceptor
  try {
    await cancelOrcamento(Number(pendingCancelId.value));

    eventBus.emit('globalMessage.success', t('Orçamento cancelado com sucesso'));
    eventBus.emit('orcamento.saved');

    showCancelModal.value = false;
    closeModal();
  } catch (error) {
    log('OrcamentosModal', 'confirmCancelOrcamento', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', message || t('Erro ao cancelar orçamento'));
  } finally {
    pendingCancelId.value = null;
  }
};

const handleDeleteOrcamento = (budgetId: Id): void => {
  log('OrcamentosModal', 'handleDeleteOrcamento', 'Delete requested', budgetId);
  pendingDeleteId.value = budgetId;
  showDeleteModal.value = true;
};

const confirmDeleteOrcamento = async (): Promise<void> => {
  log('OrcamentosModal', 'confirmDeleteOrcamento', 'Confirming delete', pendingDeleteId.value);

  if (!pendingDeleteId.value) return;

  // Note: Loading indicator is automatically handled by httpService global interceptor
  try {
    await deleteOrcamento(Number(pendingDeleteId.value));

    eventBus.emit('globalMessage.success', t('Orçamento excluído com sucesso'));
    eventBus.emit('orcamento.saved');

    showDeleteModal.value = false;
    closeModal();
  } catch (error) {
    log('OrcamentosModal', 'confirmDeleteOrcamento', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', message || t('Erro ao excluir orçamento'));
  } finally {
    pendingDeleteId.value = null;
  }
};

const handleReactivateOrcamento = async (budgetId: Id): Promise<void> => {
  log('OrcamentosModal', 'handleReactivateOrcamento', 'Reactivating', budgetId);

  // Note: Loading indicator is automatically handled by httpService global interceptor
  try {
    await changeStatus(Number(budgetId), 'Pendente');

    eventBus.emit('globalMessage.success', t('Orçamento reativado com sucesso'));
    eventBus.emit('orcamento.saved');

    // Reload form
    if (orcamentosFormRef.value) {
      await orcamentosFormRef.value.loadForEdit(budgetId);
    }
  } catch (error) {
    log('OrcamentosModal', 'handleReactivateOrcamento', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', message || t('Erro ao reativar orçamento'));
  }
};

const generateOrcamentoHtml = async (budgetId: Id): Promise<string> => {
  log('OrcamentosModal', 'generateOrcamentoHtml', 'Generating HTML for orcamento', budgetId);

  try {
    // Fetch the orcamento data
    const response = await fetchOrcamento(Number(budgetId));

    if (!response.dados) {
      throw new Error(response.error || 'Orçamento não encontrado');
    }

    // Extract data from response.dados
    const orcamento = (response.dados.orcamento ?? {}) as Record<string, string | number | undefined>;
    const cliente = (response.dados.cliente ?? {}) as Record<string, string | number | undefined>;
    // Use the contract Row directly — Orcamentos_BuscarOrcamentoResponse.dados.servicos
    // is typed as Agenda_BuscarCompromissoOrcamentoServicoRow[] | null. The HTML
    // generator below only reads servico_nome, orcamento_servico_qtd, servico_valor.
    const servicos = response.dados.servicos ?? [];
    
    // Generate HTML content similar to the original AngularJS implementation
    const html = `
      <meta charset="utf-8"/>
      <style>
        /* CRITICAL: Force color printing across all browsers */
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .bodyOrcamento{
          color: #252729;
          padding: 30px;
          font-family: Arial, Helvetica, sans-serif;
        }
        .headerOrcamento{
          background-color: #e2e2e2;
          padding: 20px;
          text-align: right;
        }
        .servicosOrcamento{
          margin-top: 20px;
          margin-bottom: 20px;
          padding: 20px;
          background-color: #f2f2f2;
        }
        .tableServicos{
          width: 100%;
        }
        .tableServicos td {
          border-top: 1px solid #252729 !important;
        }
        .totalOrcamento{
          background-color: #e2e2e2;
          padding: 20px;
          text-align: right;
        }
        /* Ensure all elements with backgrounds print correctly */
        div, table, td, span, p, h1, h2, h3 {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      </style>
      <div class='bodyOrcamento'>
        <h1>${t('Orçamento de serviço')}</h1>
        <div>
          <dl>
            <dt>${t('Cliente')}:</dt>
            <dd><b>${t('Nome')}:</b> <span class="text-capitalize">${cliente.cliente_nome || ''}</span></dd>
            <dd><b>${t('Telefone')}:</b> ${orcamento.orcamento_ddd || ''} ${orcamento.orcamento_telefone || ''}</dd>
          </dl>
        </div>
        <div>
          <dl>
            <dt>${t('Endereço')}:</dt>
            <dd><b>${t('Rua')}:</b> ${orcamento.orcamento_rua || ''}</dd>
            <dd><b>${t('Número')}:</b> ${orcamento.orcamento_numero || ''}</dd>
            <dd><b>${t('Bairro')}:</b> ${orcamento.orcamento_bairro || ''}</dd>
            <dd><b>${t('Complemento')}:</b> ${orcamento.orcamento_complemento || ''}</dd>
            <dd><b>${t('Localidade')}:</b> ${orcamento.orcamento_localidade_nome || orcamento.orcamento_localidade || ''}</dd>
            <dd><b>${t('Observações')}:</b> ${orcamento.orcamento_observacao || ''}</dd>
          </dl>
        </div>
        <div class="servicosOrcamento">
          <h4>${t('Serviços')}</h4>
          <${'table'} class="tableServicos">
            ${servicos.map(servico => `
              <tr>
                <td>${servico.servico_nome || ''}</td>
                <td>${servico.orcamento_servico_qtd || ''}</td>
                <td>${n(parseFloat(String(servico.servico_valor)) || 0, 'currency')}</td>
              </tr>
            `).join('')}
          </${'table'}>
        </div>
        <div class="totalOrcamento">
          <h3>${t('Total')}: ${n(parseFloat(String(orcamento.orcamento_valor)) || 0, 'currency')}</h3>
        </div>
        <div>
          <b>${t('Importante: Este orçamento tem validade de 48hs')}.</b>
        </div>
      </div>
    `;
    
    return html;
  } catch (error) {
    log('OrcamentosModal', 'generateOrcamentoHtml', 'Error', error);
    throw error;
  }
};

const handleDownloadPdf = async (budgetId: Id): Promise<void> => {
  log('OrcamentosModal', 'handleDownloadPdf', 'Downloading PDF', budgetId);

  // Note: Loading indicator is automatically handled by httpService global interceptor
  try {
    // Generate HTML content for the PDF
    const htmlContent = await generateOrcamentoHtml(budgetId);

    // Call the PDF generation endpoint (httpService shows progress bar automatically)
    const response = await downloadOrcamentoPDF(Number(budgetId), htmlContent);

    const documentoUrl = response?.dados?.documento_url;
    if (documentoUrl) {
      // Download the generated PDF file
      const downloadUrl = `/uploads/documentos/${documentoUrl}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = documentoUrl;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      eventBus.emit('globalMessage.success', t('PDF gerado com sucesso'));
    } else {
      throw new Error('Erro ao gerar PDF');
    }
  } catch (error) {
    log('OrcamentosModal', 'handleDownloadPdf', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', message || t('Erro ao gerar PDF'));
  }
};

const handleNovoOrcamentoServico = (): void => {
  log('OrcamentosModal', 'handleNovoOrcamentoServico', 'New orcamento servico via eventBus');
  eventBus.emit('modal.orcamento-servico.open', {
    orcamentoId: orcamentoId.value,
    orcamentoFranquiaId: orcamentoFranquiaId.value
  });
};

const handleEditOrcamentoServico = (servicoId: Id): void => {
  log('OrcamentosModal', 'handleEditOrcamentoServico', 'Edit orcamento servico via eventBus', servicoId);
  eventBus.emit('modal.orcamento-servico.open', {
    id: servicoId,
    orcamentoFranquiaId: orcamentoFranquiaId.value
  });
};

const handleEditCompromisso = (compromissoId: Id): void => {
  log('OrcamentosModal', 'handleEditCompromisso', 'Edit compromisso', compromissoId);

  // Emit event to parent component to handle compromisso editing
  // This allows the parent to decide how to handle compromisso editing
  eventBus.emit('compromisso.editRequested', compromissoId);

  // Also emit as a component event for direct parent handling
  emit('edit-compromisso', compromissoId);
};

// Watchers
watch(activeTabIndex, async (newTabIndex) => {
  log('OrcamentosModal', 'watch:activeTabIndex', 'Tab changed to index', newTabIndex);

  // Wait for the tab to be rendered and visible
  await nextTick();

  // Auto-size columns when switching to tabs with tables
  if (newTabIndex === 1 && orcamentoServicoTableRef.value) {
    log('OrcamentosModal', 'watch:activeTabIndex', 'Auto-sizing OrcamentoServico table');
    // Wait a bit more to ensure the tab is fully visible
    setTimeout(() => {
      if (orcamentoServicoTableRef.value) {
        if (orcamentoServicoTableRef.value.onTableVisible) {
          orcamentoServicoTableRef.value.onTableVisible();
        } else if (orcamentoServicoTableRef.value.autoSizeColumns) {
          orcamentoServicoTableRef.value.autoSizeColumns();
        }
      }
    }, 100);
  } else if (newTabIndex === 2 && compromissosDoOrcamentoTableRef.value) {
    log('OrcamentosModal', 'watch:activeTabIndex', 'Auto-sizing Compromissos table');
    // Wait a bit more to ensure the tab is fully visible
    setTimeout(() => {
      if (compromissosDoOrcamentoTableRef.value) {
        if (compromissosDoOrcamentoTableRef.value.onTableVisible) {
          compromissosDoOrcamentoTableRef.value.onTableVisible();
        } else if (compromissosDoOrcamentoTableRef.value.autoSizeColumns) {
          compromissosDoOrcamentoTableRef.value.autoSizeColumns();
        }
      }
    }, 100);
  }
});

// Event handlers for eventBus
const handleModalOpen = async (data?: OrcamentoModalOpenPayload): Promise<void> => {
  log('OrcamentosModal', 'handleModalOpen', 'Received modal open event', data);

  if (data?.id) {
    // Edit mode
    await loadForEdit(data.id);
  } else if (data?.clienteId) {
    // New with client
    await loadForClient(data.clienteId);
  } else {
    // New empty
    openModal();
  }
};

const handleModalClose = (): void => {
  log('OrcamentosModal', 'handleModalClose', 'Received modal close event');
  closeModal();
};

// Lifecycle
onMounted(() => {
  log('OrcamentosModal', 'mounted', 'Component mounted, registering eventBus listeners');
  eventBus.on('modal.orcamentos.open', handleModalOpen);
  eventBus.on('modal.orcamentos.close', handleModalClose);
});

onBeforeUnmount(() => {
  log('OrcamentosModal', 'beforeUnmount', 'Component unmounting');
});

onUnmounted(() => {
  log('OrcamentosModal', 'unmounted', 'Component unmounted, removing eventBus listeners');
  eventBus.off('modal.orcamentos.open', handleModalOpen);
  eventBus.off('modal.orcamentos.close', handleModalClose);
});

// Expose methods for parent component
defineExpose({
  openModal,
  loadForEdit,
  loadForClient,
  closeModal,
  resetForm
});
</script>

<style scoped>
/* Modal-specific styles */
.services-warning {
  margin-top: 20px;
  margin-bottom: 20px;
}

.add-service-button {
  margin-top: 20px;
  margin-bottom: 20px;
}
</style>

